import { useEffect, useMemo, useState } from 'react';
import { DBI_TOPCHARTS, useDispatch } from 'umi';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';

/**
 * Convert string array to options format
 */
const getOptionsFromStringArray = (data: string[]) =>
  data?.map((item) => ({
    value: item,
    text: item,
  })) || [];

const getLocaleTextMap = (data: string[]) => {
  const obj: { [key: string]: string } = {};
  data?.forEach((item) => {
    if (item && !obj[item]) {
      obj[item] = item;
    }
  });
  return obj;
};

/**
 * Get combine (PC/Console) game engine options from cond API with filter_game_engine target
 * API payload: { gameEngineCombine: { target: 'filter_game_engine', params: { entity_type: 'combine' } } }
 * API response: { gameEngineCombine: ["ZnZin", "001", "2D Fighter Maker 2nd", ...] }
 */
function useCombineGameEngineOptions() {
  const dispatch = useDispatch();

  const [gameEngineList, setGameEngineList] = useState<string[]>([]);

  const gameEngineCombine = useQueryDefinition('gameEngineCombine');

  useEffect(() => {
    // Only process when data is available (not undefined)
    // Handle null, empty array, or valid array
    if (gameEngineCombine === undefined) {
      return;
    }

    try {
      const gameEngineData = Array.isArray(gameEngineCombine) ? gameEngineCombine : [];
      const sortedData = [...gameEngineData].sort((a, b) =>
        (a ?? '').localeCompare(b ?? ''),
      );
      setGameEngineList(sortedData);
    } catch (e) {
      console.error('Error processing combine game engine data:', e);
      setGameEngineList([]);
    }
  }, [gameEngineCombine]);

  const combineGameEngineOptions = useMemo(
    () => getOptionsFromStringArray(gameEngineList),
    [gameEngineList],
  );

  const combineGameEngineLocaleTextMap = useMemo(
    () => getLocaleTextMap(gameEngineList),
    [gameEngineList],
  );

  useEffect(() => {
    if (Object.keys(combineGameEngineLocaleTextMap).length > 0) {
      dispatch({
        type: `${DBI_TOPCHARTS}/setGameEngineLocaleTextMap`,
        payload: combineGameEngineLocaleTextMap,
      });
    }
  }, [dispatch, combineGameEngineLocaleTextMap]);

  return {
    combineGameEngineOptions,
    combineGameEngineLocaleTextMap,
  };
}

export default useCombineGameEngineOptions;
